import { useEffect, useState } from "react";
import { CalendarIcon, Loader2, HeartHandshake, FileCheck2 } from "lucide-react";
import { format, startOfToday } from "date-fns";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { PASTORAL, EVENT_SECTION, PROCESS_NOTE } from "@/lib/pastoral";
import { scheduleLabel, isoWeekday } from "@/lib/schedule";
import { TIMES } from "@/components/staff/categories";
import { publicApi, errMsg } from "@/lib/api";
import { useVisitor } from "@/lib/visitor";
import { useContent } from "@/lib/content-store";

const keyOf = (section, k) => `${section} – ${k}`;

const Picker = ({ options, value, onChange, testId }) => (
  <Select value={value || undefined} onValueChange={onChange}>
    <SelectTrigger data-testid={testId}><SelectValue placeholder="Choose one" /></SelectTrigger>
    <SelectContent className="max-h-64 bg-white">{options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
  </Select>
);

const DateField = ({ value, onChange, blocked }) => (
  <Popover>
    <PopoverTrigger asChild>
      <Button type="button" variant="outline" className="w-full justify-start font-normal" data-testid="pastoral-date-button">
        <CalendarIcon className="mr-2 h-4 w-4" />{value ? format(new Date(`${value}T00:00`), "MMM d, yyyy") : "Pick a date"}
      </Button>
    </PopoverTrigger>
    <PopoverContent className="w-auto bg-white p-0" align="start">
      <Calendar mode="single" selected={value ? new Date(`${value}T00:00`) : undefined}
        disabled={(d) => d < startOfToday() || blocked.includes(isoWeekday(d))}
        onSelect={(d) => d && onChange(format(d, "yyyy-MM-dd"))} />
    </PopoverContent>
  </Popover>
);

const Field = ({ label, children }) => <div className="space-y-1.5"><Label className="text-xs font-semibold text-slate-600">{label}</Label>{children}</div>;

export const PastoralForm = ({ service, onClose }) => {
  const cfg = PASTORAL[service];
  const { profile, setProfile } = useVisitor();
  const content = useContent();
  const [vals, setVals] = useState({});
  const [ministers, setMinisters] = useState([]);
  const [blocked, setBlocked] = useState([0]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (v) => setVals((p) => ({ ...p, [k]: v }));
  const services = (content.schedules || []).map(scheduleLabel);

  useEffect(() => {
    if (!service) return;
    setDone(false);
    setVals({ [keyOf("Requested by", "Name")]: profile?.nickname || "", [keyOf("Requested by", "Mobile Number")]: profile?.mobile || "", [keyOf("Requested by", "Email")]: profile?.email || "" });
    publicApi.get("/public/ministers").then((r) => setMinisters(r.data.filter((m) => m.services.includes(service)).map((m) => m.name))).catch(() => {});
    publicApi.get("/public/pastoral-settings").then((r) => setBlocked(r.data.blocked_weekdays)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [service]);

  const submit = async (e) => {
    e.preventDefault();
    const name = (vals[keyOf("Requested by", "Name")] || "").trim();
    const phone = (vals[keyOf("Requested by", "Mobile Number")] || "").trim();
    const email = (vals[keyOf("Requested by", "Email")] || "").trim();
    if (!name || phone.length < 7) return toast.error("Please share your name and mobile number so we can reach you.");
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) return toast.error("Please share your email so we can send you a receipt and confirmation.");
    if (cfg.event && !vals[keyOf(EVENT_SECTION, "Date")]) return toast.error("Please choose a preferred date.");
    const details = { Service: cfg.title };
    const skip = ["Name", "Mobile Number", "Email"].map((k) => keyOf("Requested by", k));
    Object.entries(vals).forEach(([k, v]) => { if (v && !skip.includes(k)) details[k] = v; });
    setBusy(true);
    try {
      await publicApi.post("/requests", { action: "pastoral_care", visitor_id: profile?.id || "", name, phone, email, details });
      setProfile({ ...(profile || {}), id: profile?.id || "", nickname: name, mobile: phone, email });
      setDone(true);
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  const renderField = (section, f) => {
    const k = keyOf(section, f.k);
    const testId = `pastoral-${section}-${f.k}`.toLowerCase().replace(/[^a-z0-9]+/g, "-");
    const label = `${f.label || f.k}${f.req ? " *" : ""}`;
    if (f.type === "service") return <Field key={k} label={label}><Picker options={services} value={vals[k]} onChange={set(k)} testId={testId} /></Field>;
    if (f.type === "select") return <Field key={k} label={label}><Picker options={f.options} value={vals[k]} onChange={set(k)} testId={testId} /></Field>;
    if (f.type === "textarea") return <div key={k} className="sm:col-span-2"><Field label={label}><Textarea rows={3} value={vals[k] || ""} onChange={(e) => set(k)(e.target.value)} data-testid={testId} /></Field></div>;
    return <Field key={k} label={label}><Input value={vals[k] || ""} onChange={(e) => set(k)(e.target.value)} data-testid={testId} /></Field>;
  };

  const ev = (k) => keyOf(EVENT_SECTION, k);
  const L = cfg?.eventLabels;
  return (
    <Dialog open={!!cfg} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto rounded-3xl bg-white sm:max-w-2xl" data-testid="pastoral-form-dialog">
        <DialogTitle className="font-display text-2xl font-bold tracking-tight text-[#0A3A6B]">{cfg?.title}</DialogTitle>
        <DialogDescription className="text-slate-600">{cfg?.text}</DialogDescription>
        {done ? (
          <div className="py-6 text-center" data-testid="pastoral-form-success">
            <span className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-[#EBF3FA] text-[#1D64B4]"><HeartHandshake className="h-7 w-7" /></span>
            <h3 className="mt-4 text-xl font-semibold text-slate-900">Thank you for reaching out! We've received your request.</h3>
            <p className="mt-2 text-sm text-slate-600">A receipt is on its way to your email. Our Pastoral Admin will check the schedule with our pastors and get back to you. Please wait for our <strong>final confirmation</strong> before making any arrangements. God bless you!</p>
            <Button onClick={onClose} className="mt-6 rounded-full bg-[#0A3A6B] px-8 hover:bg-[#1D64B4]" data-testid="pastoral-form-close">Close</Button>
          </div>
        ) : cfg && (
          <form onSubmit={submit} className="space-y-6" data-testid={`pastoral-form-${service}`}>
            {cfg.requirements.length > 0 && (
              <div className="rounded-2xl bg-[#EBF3FA] p-4 text-sm text-[#0A3A6B]" data-testid="pastoral-requirements">
                <p className="flex items-center gap-2 font-semibold"><FileCheck2 className="h-4 w-4" /> Please prepare and submit the following documents:</p>
                <ol className="mt-2 list-decimal space-y-1 pl-6">{cfg.requirements.map((r) => <li key={r}>{r}</li>)}</ol>
              </div>
            )}
            {cfg.sections.map((s) => (
              <fieldset key={s.title} className="space-y-3">
                <legend className="text-xs font-semibold uppercase tracking-wider text-[#1D64B4]">{s.label || s.title}</legend>
                <div className="grid gap-3 sm:grid-cols-2">{s.fields.map((f) => renderField(s.title, f))}</div>
              </fieldset>
            ))}
            <fieldset className="space-y-3">
              <legend className="text-xs font-semibold uppercase tracking-wider text-[#1D64B4]" data-testid="pastoral-event-legend">{L.title}</legend>
              <div className="grid gap-3 sm:grid-cols-2">
                {cfg.event && <>
                  <Field label={`${L.date} *`}><DateField value={vals[ev("Date")]} onChange={set(ev("Date"))} blocked={blocked} /></Field>
                  <Field label={L.time}><Picker options={TIMES.map((t) => t.label)} value={vals[ev("Time")]} onChange={set(ev("Time"))} testId="pastoral-time" /></Field>
                  <Field label={L.venue}><Input value={vals[ev("Venue")] || ""} onChange={(e) => set(ev("Venue"))(e.target.value)} data-testid="pastoral-venue" /></Field>
                  <Field label={L.address}><Input value={vals[ev("Complete Address")] || ""} onChange={(e) => set(ev("Complete Address"))(e.target.value)} data-testid="pastoral-address" /></Field>
                </>}
                <Field label={L.minister}><Picker options={ministers} value={vals[ev("Preferred Minister")]} onChange={set(ev("Preferred Minister"))} testId="pastoral-minister" /></Field>
              </div>
            </fieldset>
            <p className="rounded-2xl bg-slate-50 p-4 text-xs text-slate-500">{PROCESS_NOTE}</p>
            <Button type="submit" disabled={busy} className="w-full rounded-full bg-[#0A3A6B] hover:bg-[#1D64B4]" data-testid="pastoral-submit-button">
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Send request
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
